import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { map } from 'rxjs/operators';
import {
  DerivativeAttachmentDto,
  DerivativeAttachmentType,
} from '@interfaces/a-organizar/derivative-attachment';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { FormErrorHandlerService } from '@services/helpers/form-error-handler.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { DerivativeAttachmentService } from '@services/http/derivative-attachment.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { DocumentAttachmentFile } from '@src/app/shared/components/document-attachment/document-attachment.component';
import { SharedModule } from '@src/app/shared/shared.module';

@Component({
  selector: 'app-modal-derivative-attachments',
  templateUrl: './modal-derivative-attachments.component.html',
  styleUrls: ['./modal-derivative-attachments.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
})
export class ModalDerivativeAttachmentsComponent extends VaultBaseComponent implements OnChanges {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  @Input() operationId: string | null = null;
  @Output() attachmentUploaded = new EventEmitter<void>();

  /**
   * Card 3902: enviar e remover anexo pedem `DerivativeCalculator.ATTACHMENT` (POST/DELETE
   * `operations/ndf/operation/.../attachment`). Sem ela a modal só lista e baixa.
   */
  readonly canManageAttachments = toSignal(
    inject(PermissionsService)
      .hasPermission(Permissions.DerivativeCalculator.ATTACHMENT)
      .pipe(map(Boolean)),
    { initialValue: false }
  );

  // File upload properties
  documents: DocumentAttachmentFile[] = [];
  selectedAttachmentType: string | null = null;

  // Existing attachments
  existingAttachments: DerivativeAttachmentDto[] = [];

  // Options for attachment types
  attachmentTypeOptions: Option[] = [
    {
      label: 'Nota de Negociação',
      value: DerivativeAttachmentType.TradingNote,
    },
    {
      label: 'Carta de Liquidação',
      value: DerivativeAttachmentType.SettlementLetter,
    },
  ];

  constructor(
    translateService: TranslateService,
    private derivativeAttachmentService: DerivativeAttachmentService,
    private formErrorHandler: FormErrorHandlerService,
    private toastHelper: ToastHelperService
  ) {
    super(translateService);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['operationId'] && this.operationId) {
      this.loadExistingAttachments();
    }
  }

  async open(operationId: string) {
    this.operationId = operationId;
    this.documents = [];
    this.selectedAttachmentType = null;
    this.existingAttachments = [];

    this.isLoading = true;
    this.loadExistingAttachments();

    this.dialogRef?.open('', '', '', 'Cancelar', '', 'modal-derivative-attachments', false, true);
  }

  private loadExistingAttachments(): void {
    if (!this.operationId) return;

    this.derivativeAttachmentService.getAttachments(this.operationId).subscribe({
      next: attachments => {
        this.existingAttachments = attachments || [];
        this.isLoading = false;
      },
      error: error => {
        this.formErrorHandler.showToast(error, 'Erro ao carregar anexos existentes');
        this.isLoading = false;
      },
    });
  }

  isFormValid(): boolean {
    return this.documents.length > 0 && this.documents.every(doc => doc.file.size > 0);
  }

  onFileSelected(files: File[]): void {
    if (files.length > 0) {
      this.documents = files.map(file => ({
        file: file,
        displayName: file.name,
        downloadLink: null,
        id: null,
        errorMessage: null,
        isInvalid: false,
        isValid: true,
        tooltipMessage: null,
      }));
    }
  }

  onRemoveFile(file: DocumentAttachmentFile): void {
    this.documents = this.documents.filter(doc => doc !== file);
  }

  submitUpload(): void {
    if (!this.isFormValid() || !this.operationId) {
      this.toastHelper.showToastError('Selecione pelo menos um arquivo para upload');
      return;
    }

    this.isLoading = true;
    const files = this.documents.map(doc => doc.file);

    this.derivativeAttachmentService
      .uploadAttachments(this.operationId, files, this.selectedAttachmentType ?? undefined)
      .subscribe({
        next: () => {
          this.toastHelper.showToastSuccess('Anexos enviados com sucesso');
          this.attachmentUploaded.emit();
          this.documents = [];
          this.selectedAttachmentType = null;
          this.loadExistingAttachments(); // Reload existing attachments
          this.isLoading = false;
        },
        error: error => {
          this.formErrorHandler.showToast(error, 'Erro ao enviar anexos');
          this.isLoading = false;
        },
      });
  }

  downloadAttachment(attachment: DerivativeAttachmentDto): void {
    this.derivativeAttachmentService.getDownloadLink(attachment.id).subscribe({
      next: downloadUrl => {
        if (downloadUrl) {
          window.open(downloadUrl, '_blank');
        } else {
          this.toastHelper.showToastError('Erro ao obter link de download');
        }
      },
      error: error => {
        this.formErrorHandler.showToast(error, 'Erro ao obter link de download');
      },
    });
  }

  deleteAttachment(attachment: DerivativeAttachmentDto): void {
    if (!this.operationId) return;

    this.isLoading = true;
    this.derivativeAttachmentService.deleteAttachment(this.operationId, attachment.id).subscribe({
      next: () => {
        this.toastHelper.showToastSuccess('Anexo removido com sucesso');
        this.loadExistingAttachments(); // Reload existing attachments
        this.attachmentUploaded.emit(); // Notify parent
      },
      error: error => {
        this.formErrorHandler.showToast(error, 'Erro ao remover anexo');
        this.isLoading = false;
      },
    });
  }

  getAttachmentTypeLabel(type: string | null): string {
    if (type === null) return 'Não especificado';

    const option = this.attachmentTypeOptions.find(opt => opt.value === type);
    return option?.label || 'Tipo desconhecido';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
